// The artillery shot recipes, mockup round 049 (R1007).
//
// One entry per look id of `labels.toml shot_artillery_styles`, keyed by the
// id. The artillery role fires these (R1009). The rules of the file are the
// rules of `combat-recipes-melee.ts`: PENDING names every card that is not
// built, the test refuses an id in neither table, a figure comes out of the
// row and a proportion stays a named constant here.
//
// **The ten cards are built and PENDING is empty.** The source of each one is
// its card in `archive/mockups/049-shots-artillery.html`, array `SHOTS_049`,
// and the port keeps the card's geometry, timing and salvo shape at 1× map
// scale. Three translations differ from canvas-2D, and each is named on its
// own recipe:
//
//   - A CANVAS GRADIENT is a baked atlas ramp here, so card 06 STAR SHELL —
//     the one card the page priced "expensive", for two live radial gradients
//     a frame — costs two quads and stands beside its neighbours.
//   - A STROKED POLYLINE is n round-capped segments of the segment sink, so
//     card 03 TRACK ARC draws its trajectory as 24 joined segments in place of
//     one path. A round cap at a joint is what `lineJoin: round` drew.
//   - A ROTATED `fillRect` is a quad of the flat texel, so the square shell of
//     card 16 FUSE SPARK turns with `rect` and needs no path.
//
// A figure the card states is a row field of `data/presentation/combat.toml`.
// The row holds two alphas and the cards hold more, so an alpha a row cannot
// carry stays a named constant here — the rule `FLASH_HEAD_ALPHA` of the melee
// file already follows.
//
// This is a leaf (`leaves.test.ts`): it imports the kit and the generated
// pack, and never an engine.

import {
  COMBAT_WHITE, DEEP_INK, NEUTRAL_INK, PALE_INK,
  arcOf, atOn, blob, dart, disc, flare, glint, hash, line, rect, ring, trail,
  type Pt, type ShotRecipeFn,
} from './combat-recipe-kit';

// ---------------------------------------------------------------------------
// The proportions
// ---------------------------------------------------------------------------

/** siege-salvo, card 11: the shadow on the floor, as a share of the shell. */
const SIEGE_SHADOW_K = 0.8;
/** siege-salvo: how dark the shadow stands. It is the deep ink at half ink. */
const SIEGE_SHADOW_ALPHA = 0.5;
/** siege-salvo: the dash pattern of the aim line, in world px. A dash is the
 *  shape of a line and not a figure a designer moves. */
const SIEGE_DASH_ON = 3, SIEGE_DASH_OFF = 6;
/** siege-salvo: how much of the flight the aim line shows for. */
const SIEGE_AIM_SPAN = 0.3;

/** shell-lob, card 01: how far the shadow shrinks at the apex, as a share of
 *  its own radius. The card writes `2.6 - 0.8 * s`. */
const LOB_SHADOW_SHRINK = 0.3077;
/** shell-lob: the shadow's alpha on the floor, and how far the apex pales it.
 *  The card writes `0.6 * (1 - 0.5 * s)`. */
const LOB_SHADOW_ALPHA = 0.6, LOB_SHADOW_DIM = 0.5;
/** shell-lob: how far the shell grows at the apex, as a share of its radius.
 *  The card writes `1.6 + 1.0 * s`, so the shell reads as nearer the eye. */
const LOB_SHELL_GROW = 0.625;
/** shell-lob: how far back down the arc the short trail reaches, as a share
 *  of the flight. It is one frame of the card's own clock. */
const LOB_TRAIL_STEP = 0.04;

/** track-arc, card 03: the shadow on the floor, as a share of the shell. */
const ARC_SHADOW_K = 0.8182;
/** track-arc: how dark the shadow stands under the drawn trajectory. */
const ARC_SHADOW_ALPHA = 0.45;

/** star-shell, card 06: the share of the flight the shell climbs for. It
 *  ignites at the apex, which is the half of a sine arc. */
const STAR_IGNITE = 0.5;
/** star-shell: the white core inside the climbing shell, as a share of it. */
const STAR_CORE_K = 0.4;
/** star-shell: the shell's and the core's alpha during the climb. */
const STAR_CLIMB_ALPHA = 0.95, STAR_CORE_ALPHA = 0.9;
/** star-shell: the shadow on the floor during the climb. */
const STAR_SHADOW_ALPHA = 0.5;
/** star-shell: how far the flicker moves the light, as a share of its radius.
 *  The card writes `14 + 3 * sin(t * 20)`. */
const STAR_FLICKER_K = 0.2143;
/** star-shell: the four-point glint on the light, as a share of the light. */
const STAR_GLINT_K = 0.4286;
/** star-shell: the glint's own alpha. */
const STAR_GLINT_ALPHA = 0.9;
/** star-shell: how far the lit disc of water closes as the light falls, as a
 *  share of its own radius. The card writes `34 - 14 * u`. */
const STAR_WATER_SHRINK = 0.4118;
/** star-shell: a spark's sideways drift, as a share of how far it falls. */
const STAR_SPARK_DRIFT_K = 0.5556;
/** star-shell: a spark's own radius, as a share of the shell. */
const STAR_SPARK_K = 0.4;
/** star-shell: a spark's alpha at birth, and how many times a spark falls
 *  over the drift. Each spark runs its own clock off a pure hash. */
const STAR_SPARK_ALPHA = 0.8, STAR_SPARK_CYCLES = 3, STAR_SPARK_SEED = 31;

/** rail-dart, card 07: the dart's alpha. It never fades: the dart IS the
 *  round, and the glowing line behind it is what fades. */
const RAIL_DART_ALPHA = 0.95;

/** sabot, card 09: the share of the flight the sleeve takes to fall away. */
const SABOT_SHED = 0.35;
/** sabot: how fast a petal falls astern, as a share of the round's own
 *  progress. The four petals take the two rates in turn. */
const SABOT_RATE_SLOW = 0.36, SABOT_RATE_FAST = 0.5;
/** sabot: where a petal stands before it swings, as a share of its swing. */
const SABOT_PETAL_BASE_K = 0.1786;
/** sabot: how far a petal sinks as it goes, as a share of its swing. */
const SABOT_PETAL_SINK_K = 0.4286;
/** sabot: a petal's half beam, as a share of its half length. */
const SABOT_PETAL_THIN = 0.4;
/** sabot: how far a petal turns as it falls away, in radians. */
const SABOT_PETAL_TURN = 0.9;
/** sabot: where the white core streak starts, as a share of its far end. */
const SABOT_CORE_NEAR_K = 0.25;
/** sabot: the core streak's alpha. */
const SABOT_CORE_ALPHA = 0.9;

/** heavy-round, card 12: the shadow's half beam, as a share of its half
 *  length, and how far under the round it sits, in world px. */
const HEAVY_SHADOW_THIN = 0.5833, HEAVY_SHADOW_DROP = 2;
/** heavy-round: how dark the shadow astern stands. */
const HEAVY_SHADOW_ALPHA = 0.5;
/** heavy-round: the two arcs of compressed water ahead of the round: half the
 *  sweep of each, in radians. */
const HEAVY_ARC1_HALF = 0.8, HEAVY_ARC2_HALF = 0.5;
/** heavy-round: the outer arc's radius and alpha, as shares of the inner. */
const HEAVY_ARC2_K = 1.5, HEAVY_ARC2_ALPHA_K = 0.5;
/** heavy-round: the white stripe's half length, as a share of the round, and
 *  its alpha. The stripe turns as the round rolls. */
const HEAVY_STRIPE_K = 0.8, HEAVY_STRIPE_ALPHA = 0.8;

/** fuse-spark, card 16: how dark the shadow on the floor stands. */
const FUSE_SHADOW_ALPHA = 0.5;
/** fuse-spark: which corner of the tumbling shell the fuse sits on, in
 *  radians off the shell's own axis. It is the trailing corner. */
const FUSE_CORNER = 2.36;
/** fuse-spark: the hash a strobe frame must beat for the fuse to be lit. */
const FUSE_STROBE_GATE = 0.35;
/** fuse-spark: the unlit glint's reach and the two alphas of the fuse. */
const FUSE_DIM_K = 0.5714, FUSE_LIT_ALPHA = 0.95, FUSE_DIM_ALPHA = 0.5;
/** fuse-spark: how far back down the arc one spark stands, as a share of the
 *  flight, and how far a spark scatters and sags, as shares of the shell. */
const FUSE_SPARK_STEP = 0.025;
const FUSE_SPARK_SPREAD_K = 1.5385, FUSE_SPARK_SAG_K = 0.9615;
/** fuse-spark: a spark's own radius, as a share of the shell, how far an
 *  unlit frame dims it, and the seed of its scatter. */
const FUSE_SPARK_R_K = 0.2692, FUSE_SPARK_DIM = 0.6, FUSE_SPARK_SEED = 51;

/** muzzle-bloom, card 17: how much of the flight the gun's own mark lasts. */
const BLOOM_SPAN = 0.35;
/** muzzle-bloom: how far ahead of the hull the muzzle stands, in world px. */
const BLOOM_MUZZLE_PX = 5;
/** muzzle-bloom: where the bloom and the ring open from, as shares of their
 *  widest radius. The card writes `4 + 10 * u` and `3 + 16 * u`. */
const BLOOM_START_K = 0.2857, RING_START_K = 0.1579;
/** muzzle-bloom: the recoil wash. Its half length as a share of the bloom,
 *  its half beam as a share of that, how far it grows, how far it rolls
 *  astern, and its alpha. */
const BLOOM_WASH_K = 0.5714, BLOOM_WASH_THIN = 0.5;
const BLOOM_WASH_GROW = 0.75, BLOOM_WASH_ROLL = 0.3846, BLOOM_WASH_ALPHA = 0.25;
/** muzzle-bloom: the round's tail and head alphas. The head never fades. */
const BLOOM_TAIL_ALPHA = 0.5, BLOOM_HEAD_ALPHA = 0.95;

/** beam-pulse, card 19: the head's alpha. The head never fades. */
const PULSE_HEAD_ALPHA = 0.95;

/** The two reused points of this file (R131). */
const P0: Pt = { x: 0, y: 0 };
const P1: Pt = { x: 0, y: 0 };

export const RECIPES: Readonly<Record<string, ShotRecipeFn>> = {
  /**
   * Card 11, SIEGE SALVO (the AUTO look).
   *
   * Three shells in a row on a high arc, and the aim line shows on the water
   * for a moment as the first shell leaves. The shell climbs on a half sine,
   * so its shadow stays on the ground line and the eye reads the height.
   */
  'siege-salvo'(c) {
    const r = c.row;
    const g = atOn(c.d, c.from, c.p, 0, P0);
    const height = r.lengthPx * Math.sin(Math.PI * c.p);
    if (c.k === 0 && c.p < SIEGE_AIM_SPAN) {
      line(
        c, c.from.x, c.from.y, c.to.x, c.to.y, r.widthPx, c.ink,
        r.alpha2 * (1 - c.p / SIEGE_AIM_SPAN), SIEGE_DASH_ON, SIEGE_DASH_OFF,
      );
    }
    disc(c, g.x, g.y, r.rPx * SIEGE_SHADOW_K, DEEP_INK, SIEGE_SHADOW_ALPHA);
    disc(c, g.x, g.y - height, r.rPx, c.ink, r.alpha);
    disc(c, g.x, g.y - height, r.r2Px, COMBAT_WHITE, r.alpha);
  },

  /**
   * Card 01, SHELL LOB.
   *
   * The plain lob of the family. The shell climbs on a half sine, grows as it
   * nears the eye, and comes down on the target. Its dark shadow crosses the
   * floor under it and is palest and smallest at the apex, so the height
   * reads without a scale. A short pale trail hangs one frame behind it.
   */
  'shell-lob'(c) {
    const r = c.row;
    const s = Math.sin(Math.PI * c.p);
    const height = r.lengthPx * s;
    const g = atOn(c.d, c.from, c.p, 0, P0);
    disc(
      c, g.x, g.y, r.rPx * (1 - LOB_SHADOW_SHRINK * s), DEEP_INK,
      LOB_SHADOW_ALPHA * (1 - LOB_SHADOW_DIM * s),
    );
    const pr = c.p > LOB_TRAIL_STEP ? c.p - LOB_TRAIL_STEP : 0;
    const g0 = atOn(c.d, c.from, pr, 0, P1);
    const h0 = r.lengthPx * Math.sin(Math.PI * pr);
    line(c, g.x, g.y - height, g0.x, g0.y - h0, r.widthPx, PALE_INK, r.alpha2);
    disc(
      c, g.x, g.y - height, r.r2Px * (1 + LOB_SHELL_GROW * s),
      COMBAT_WHITE, r.alpha,
    );
  },

  /**
   * Card 03, TRACK ARC.
   *
   * The shell draws its whole trajectory: a thin arc in the faction ink grows
   * out of the muzzle behind it and stands until the shell lands. The card
   * strokes one path of `count + 1` points; the segment sink has no path, so
   * the recipe writes `count` round-capped segments, which is the picture the
   * card's own `lineJoin: round` drew.
   */
  'track-arc'(c) {
    const r = c.row;
    const n = r.count > 0 ? r.count : 1;
    let px = c.from.x, py = c.from.y;
    for (let i = 1; i <= n; i++) {
      const u = (c.p * i) / n;
      const q = atOn(c.d, c.from, u, 0, P1);
      const qy = q.y - r.lengthPx * Math.sin(Math.PI * u);
      line(c, px, py, q.x, qy, r.widthPx, c.ink, r.alpha2);
      px = q.x; py = qy;
    }
    const g = atOn(c.d, c.from, c.p, 0, P0);
    const height = r.lengthPx * Math.sin(Math.PI * c.p);
    disc(c, g.x, g.y, r.rPx * ARC_SHADOW_K, DEEP_INK, ARC_SHADOW_ALPHA);
    disc(c, g.x, g.y - height, r.rPx, c.ink, r.alpha);
    disc(c, g.x, g.y - height, r.r2Px, COMBAT_WHITE, r.alpha);
  },

  /**
   * Card 06, STAR SHELL.
   *
   * The shell climbs to the apex and ignites. A white light then drifts down
   * onto the target over the second half of the flight, lights a disc of
   * water under it, and three sparks fall off it.
   *
   * The card is priced "expensive" because canvas built two radial gradients
   * a frame. The kit's `flare` is a BAKED atlas ramp the quad sink tints, so
   * the ported look writes two quads and costs what its neighbours cost.
   */
  'star-shell'(c) {
    const r = c.row;
    if (c.p < STAR_IGNITE) {
      const g = atOn(c.d, c.from, c.p, 0, P0);
      const height = r.lengthPx * Math.sin(Math.PI * c.p);
      disc(c, g.x, g.y, r.rPx, DEEP_INK, STAR_SHADOW_ALPHA);
      disc(c, g.x, g.y - height, r.rPx, c.ink, STAR_CLIMB_ALPHA);
      disc(c, g.x, g.y - height, r.rPx * STAR_CORE_K, COMBAT_WHITE, STAR_CORE_ALPHA);
      return;
    }
    const u = (c.p - STAR_IGNITE) / (1 - STAR_IGNITE);
    const g = atOn(c.d, c.from, c.p, 0, P0);
    const y = g.y - r.lengthPx * (1 - u);
    flare(c, g.x, g.y, r.length2Px * (1 - STAR_WATER_SHRINK * u), PALE_INK, r.alpha2);
    flare(
      c, g.x, y, r.r2Px * (1 + STAR_FLICKER_K * Math.sin(c.t * r.speed)),
      COMBAT_WHITE, r.alpha,
    );
    glint(c, g.x, y, r.r2Px * STAR_GLINT_K, r.widthPx, COMBAT_WHITE, STAR_GLINT_ALPHA);
    for (let i = 0; i < r.count; i++) {
      const h = hash(c.k * 3 + i, STAR_SPARK_SEED);
      const age = (u * STAR_SPARK_CYCLES + h) % 1;
      disc(
        c, g.x + (h - 0.5) * r.width2Px * STAR_SPARK_DRIFT_K * age,
        y + r.width2Px * age, r.rPx * STAR_SPARK_K, COMBAT_WHITE,
        STAR_SPARK_ALPHA * (1 - age),
      );
    }
  },

  /**
   * Card 07, RAIL DART.
   *
   * The Falconet as a rail gun. The dart crosses the whole gap in a fifth of
   * a second and leaves a straight glowing line from the muzzle: a fat band
   * of the faction ink and a white core inside it, both fading as it flies.
   */
  'rail-dart'(c) {
    const r = c.row;
    const f = 1 - c.p;
    const h = atOn(c.d, c.from, c.p, 0, P0);
    line(c, c.from.x, c.from.y, h.x, h.y, r.widthPx, c.ink, r.alpha * f);
    line(c, c.from.x, c.from.y, h.x, h.y, r.width2Px, COMBAT_WHITE, r.alpha2 * f);
    dart(c, h.x, h.y, c.d.ang, r.lengthPx, r.rPx, COMBAT_WHITE, RAIL_DART_ALPHA);
  },

  /**
   * Card 09, SABOT.
   *
   * A long dart in the faction ink leaves inside a sleeve. The four petals of
   * the sleeve fall away over the first third of the flight, two to each
   * side, turning and sinking as they go, and the dart flies on alone with a
   * white core astern of its tail.
   */
  'sabot'(c) {
    const r = c.row;
    const h = atOn(c.d, c.from, c.p, 0, P0);
    if (c.p < SABOT_SHED) {
      const u = c.p / SABOT_SHED;
      const half = r.count / 2;
      for (let i = 0; i < r.count; i++) {
        const side = i < half ? -1 : 1;
        const rate = i % 2 ? SABOT_RATE_FAST : SABOT_RATE_SLOW;
        const off = side * r.length2Px * (SABOT_PETAL_BASE_K + u);
        const q = atOn(c.d, c.from, c.p * rate, off, P1);
        blob(
          c, q.x, q.y + r.length2Px * SABOT_PETAL_SINK_K * u * u,
          r.r2Px, r.r2Px * SABOT_PETAL_THIN,
          c.d.ang + side * SABOT_PETAL_TURN * u, NEUTRAL_INK, r.alpha2 * (1 - u),
        );
      }
    }
    dart(c, h.x, h.y, c.d.ang, r.lengthPx, r.rPx, c.ink, r.alpha);
    const near = r.width2Px * SABOT_CORE_NEAR_K;
    line(
      c, h.x - c.d.ux * near, h.y - c.d.uy * near,
      h.x - c.d.ux * r.width2Px, h.y - c.d.uy * r.width2Px,
      r.widthPx, COMBAT_WHITE, SABOT_CORE_ALPHA,
    );
  },

  /**
   * Card 12, HEAVY ROUND.
   *
   * The weight of the siege gun in one mark. One slow round crosses alone: a
   * disc in the faction ink with a white stripe that turns as it rolls, a
   * dark shadow astern of it, and two arcs of compressed water ahead.
   */
  'heavy-round'(c) {
    const r = c.row;
    const h = atOn(c.d, c.from, c.p, 0, P0);
    const rot = c.t * r.speed + c.k;
    blob(
      c, h.x - c.d.ux * r.length2Px,
      h.y - c.d.uy * r.length2Px + HEAVY_SHADOW_DROP,
      r.lengthPx, r.lengthPx * HEAVY_SHADOW_THIN, c.d.ang,
      DEEP_INK, HEAVY_SHADOW_ALPHA,
    );
    arcOf(
      c, h.x, h.y, r.r2Px, r.widthPx,
      c.d.ang - HEAVY_ARC1_HALF, 2 * HEAVY_ARC1_HALF, PALE_INK, r.alpha2,
    );
    arcOf(
      c, h.x, h.y, r.r2Px * HEAVY_ARC2_K, r.widthPx,
      c.d.ang - HEAVY_ARC2_HALF, 2 * HEAVY_ARC2_HALF, PALE_INK,
      r.alpha2 * HEAVY_ARC2_ALPHA_K,
    );
    disc(c, h.x, h.y, r.rPx, c.ink, r.alpha);
    const sx = Math.cos(rot) * r.rPx * HEAVY_STRIPE_K;
    const sy = Math.sin(rot) * r.rPx * HEAVY_STRIPE_K;
    line(
      c, h.x + sx, h.y + sy, h.x - sx, h.y - sy,
      r.width2Px, COMBAT_WHITE, HEAVY_STRIPE_ALPHA,
    );
  },

  /**
   * Card 16, FUSE SPARK.
   *
   * The old shell of a mortar, lit. A square shell tumbles over the water on
   * a lob, and the fuse on its trailing corner sputters: a glint that strobes
   * `count2` times a second, and `count` sparks that fall off the arc behind
   * it. The card's rotated `fillRect` is a quad of the flat texel here.
   */
  'fuse-spark'(c) {
    const r = c.row;
    const g = atOn(c.d, c.from, c.p, 0, P0);
    const x = g.x, y = g.y - r.lengthPx * Math.sin(Math.PI * c.p);
    const rot = c.t * r.speed + c.k;
    disc(c, g.x, g.y, r.r2Px, DEEP_INK, FUSE_SHADOW_ALPHA);
    rect(c, x, y, 2 * r.rPx, 2 * r.rPx, rot, c.ink, r.alpha);
    const on = hash(Math.floor(c.t * r.count2), c.k) > FUSE_STROBE_GATE;
    const fx = x + Math.cos(rot + FUSE_CORNER) * r.length2Px;
    const fy = y + Math.sin(rot + FUSE_CORNER) * r.length2Px;
    glint(
      c, fx, fy, on ? r.width2Px : r.width2Px * FUSE_DIM_K, r.widthPx,
      COMBAT_WHITE, on ? FUSE_LIT_ALPHA : FUSE_DIM_ALPHA,
    );
    for (let i = 1; i <= r.count; i++) {
      const u = c.p - i * FUSE_SPARK_STEP;
      if (u < 0) continue;
      const q = atOn(c.d, c.from, u, 0, P1);
      const h = hash(c.k * 5 + i, FUSE_SPARK_SEED);
      disc(
        c, q.x + (h - 0.5) * r.rPx * FUSE_SPARK_SPREAD_K,
        q.y - r.lengthPx * Math.sin(Math.PI * u) + i * r.rPx * FUSE_SPARK_SAG_K,
        r.rPx * FUSE_SPARK_R_K, COMBAT_WHITE,
        r.alpha2 * (1 - i / (r.count + 1)) * (on ? 1 : FUSE_SPARK_DIM),
      );
    }
  },

  /**
   * Card 17, MUZZLE BLOOM.
   *
   * The gun is the mark. A bloom in the faction ink opens at the muzzle, a
   * ring runs out of it, and a wash of pale water rolls astern of the shooter
   * with the recoil. The round itself is a small fast dot with a short tail.
   */
  'muzzle-bloom'(c) {
    const r = c.row;
    const h = atOn(c.d, c.from, c.p, 0, P0);
    if (c.p < BLOOM_SPAN) {
      const u = c.p / BLOOM_SPAN;
      const f = 1 - u;
      const mx = c.from.x + c.d.ux * BLOOM_MUZZLE_PX;
      const my = c.from.y + c.d.uy * BLOOM_MUZZLE_PX;
      flare(
        c, mx, my, r.lengthPx * (BLOOM_START_K + (1 - BLOOM_START_K) * u),
        c.ink, r.alpha * f,
      );
      ring(
        c, mx, my, r.length2Px * (RING_START_K + (1 - RING_START_K) * u),
        r.widthPx, c.ink, r.alpha2 * f,
      );
      const back = r.driftPx * (1 + BLOOM_WASH_ROLL * u);
      const wash = r.lengthPx * BLOOM_WASH_K * (1 + BLOOM_WASH_GROW * u);
      blob(
        c, c.from.x - c.d.ux * back, c.from.y - c.d.uy * back,
        wash, wash * BLOOM_WASH_THIN, c.d.ang, PALE_INK, BLOOM_WASH_ALPHA * f,
      );
    }
    trail(c, h, r.r2Px, r.width2Px, COMBAT_WHITE, BLOOM_TAIL_ALPHA);
    disc(c, h.x, h.y, r.rPx, COMBAT_WHITE, BLOOM_HEAD_ALPHA);
  },

  /**
   * Card 19, BEAM PULSE.
   *
   * A direct-fire energy look for the Falconet. A faint beam stands between
   * the two hulls while the salvo runs, and a bright window of `count`
   * segments runs down it, each one fainter than the one ahead of it.
   */
  'beam-pulse'(c) {
    const r = c.row;
    const h = atOn(c.d, c.from, c.p, 0, P0);
    line(c, c.from.x, c.from.y, c.to.x, c.to.y, r.widthPx, c.ink, r.alpha2);
    for (let i = 0; i < r.count; i++) {
      const q0 = i * r.lengthPx, q1 = q0 + r.lengthPx;
      line(
        c, h.x - c.d.ux * q0, h.y - c.d.uy * q0,
        h.x - c.d.ux * q1, h.y - c.d.uy * q1,
        r.width2Px, c.ink, r.alpha * (1 - i / r.count),
      );
    }
    disc(c, h.x, h.y, r.rPx, COMBAT_WHITE, PULSE_HEAD_ALPHA);
  },
};

/**
 * No card of round 049 waits.
 *
 * The table stays, because `combat-styles.ts` reads it for every field and
 * `combat-recipes-artillery.test.ts` pins that every id of
 * `shot_artillery_styles` stands in `RECIPES` or here.
 */
export const PENDING: readonly string[] = [];
